<template>
  <div v-if="loading" class="flex h-full justify-center items-center">
    <loading-spinner diameter="64px" />
  </div>

  <div class="w-full">
    <el-form
      @submit.prevent="openCashierPinDialog(formRef, registerTransaction)"
      novalidate
      :model="registerTransaction"
      label-position="top"
      class="flex flex-col items-center w-full"
      :rules="rules"
      ref="formRef"
    >
      <!-- @submit errors -->
      <alert-badge
        isDanger
        :title="error.title"
        :body="error.body"
        @dismissed="
          () => {
            error.title = ''
            error.body = ''
          }
        "
      />

      <div class="input-section w-full">
        <h1>{{ t('section') }}</h1>
        <div class="input-group w-full">
          <el-form-item
            prop="journalType"
            :label="t('transactionType')"
            class="input-label"
            ref="journalType"
            style="width: 70%"
          >
            <el-select v-model="registerTransaction.journalType" placeholder="Select" size="large">
              <el-option :label="$t('exchange.option')" value="exchange" />
              <el-option :label="$t('add')" value="add" />
              <el-option :label="$t('withdraw')" value="withdraw" />
              <!-- <el-option :label="$t('drop')" value="drop" /> -->
            </el-select>
          </el-form-item>
        </div>
      </div>

      <div class="input-section w-full" v-if="registerTransaction.journalType !== 'exchange'">
        <div class="input-group w-full">
          <el-form-item
            prop="amountAmount"
            class="label input-label w-full"
            :label="t('amount')"
            ref="amount"
            style="width: 70%"
          >
            <money-input
              id="transaction-amount"
              :currency="$ctx.currency"
              v-model="primaryCurrencyAmount"
            />
          </el-form-item>
        </div>
      </div>

      <div class="input-section w-full" v-if="registerTransaction.journalType !== 'exchange'">
        <div class="input-group" style="width: 70%">
          <el-form-item
            prop="foreignCurrency"
            :label="t('foreignCurrency')"
            class="label input-label"
            ref="amount"
          >
            <new-money-input
              id="transaction-amount"
              :currency="foreignCurrency"
              v-model="foreignCurrencyAmount"
            />
          </el-form-item>
          <div class="mt-7">X</div>
          <el-form-item
            prop="exchangeRate"
            :label="t('exchangeRate')"
            class="label input-label"
            ref="amount"
          >
            <el-input v-model="USDToIQDExchangeRate" ref="usdToIqdInput" />
          </el-form-item>
        </div>
        {{ t('total') }} =
        {{
          formatMoney(
            primaryCurrencyAmount +
              convertCurrency(
                foreignCurrencyAmount,
                USDToIQDExchangeRate,
                foreignCurrencyPrecision,
                'IQD'
              ),
            this.$ctx.currency
          )
        }}
      </div>

      <div v-if="registerTransaction.journalType === 'add'" class="input-section w-full">
        <div class="input-group w-full">
          <el-form-item
            prop="creditedAccountID"
            :label="t('chooseAccountToAddMoney')"
            class="input-label"
            style="width: 70%"
          >
            <el-select
              v-model="registerTransaction.creditedAccountID"
              :placeholder="t('selectAccount')"
              size="large"
              :trigger-on-focus="false"
              value-key="name"
              filterable
              remote
              :remote-method="searchAccounts"
              :clearable="true"
            >
              <el-option
                :key="safeAccount.id"
                :label="$ctx.locale === 'ar' ? safeAccount.arName : safeAccount.enName"
                :value="safeAccount.id"
              >
                <div class="w-full bg-green-100">
                  {{ $ctx.locale === 'ar' ? safeAccount.arName : safeAccount.enName }}
                </div>
              </el-option>

              <el-option
                v-for="account in accounts"
                :key="account.id"
                :label="$ctx.locale === 'en' ? account.enName : account.arName"
                :value="account.id"
              />
            </el-select>
          </el-form-item>
        </div>
      </div>

      <div v-if="registerTransaction.journalType === 'withdraw'" class="input-section w-full">
        <div class="input-group w-full">
          <el-form-item
            size="large"
            prop="debitedAccountID"
            :label="t('chooseAccountTransferTo')"
            class="input-label"
            label-width="200px"
            style="width: 70%"
          >
            <el-select
              v-model="registerTransaction.debitedAccountID"
              :placeholder="t('selectAccount')"
              size="large"
              :trigger-on-focus="false"
              value-key="name"
              filterable
            >
              <el-option
                v-for="safe in safes"
                :key="safe.id"
                :label="$ctx.locale === 'ar' ? safe.arName : safe.enName"
                :value="safe.id"
              >
                <div class="w-full bg-green-100">
                  {{ $ctx.locale === 'ar' ? safe.arName : safe.enName }}
                </div>
              </el-option>

              <el-option
                v-for="bank in banks"
                :key="bank.id"
                :label="$ctx.locale === 'ar' ? bank.arName : bank.enName"
                :value="bank.id"
              >
                <div class="w-full bg-blue-100">
                  {{ $ctx.locale === 'ar' ? bank.arName : bank.enName }}
                </div>
              </el-option>
            </el-select>
          </el-form-item>
        </div>
      </div>

      <div v-if="registerTransaction.journalType === 'exchange'" class="input-section w-full">
        <el-form-item
          size="large"
          :label="t('exchange.label')"
          class="input-label"
          label-width="200px"
          style="width: 70%"
        >
          <el-radio-group
            size="large"
            v-model="exchangeOption"
            :change="changeExchangeCurrency()"
            class="w-full"
          >
            <el-radio-button :label="t('exchange.USDtoIQD')" value="usdToIqd" />
            <el-radio-button :label="t('exchange.IQDtoUSD')" value="iqdToUsd" />
          </el-radio-group>
        </el-form-item>

        <div class="input-group" label-width="50px" style="width: 70%">
          <el-form-item
            prop="exchangeAMount"
            :label="t('exchange.amountOut')"
            class="label input-label"
            ref="exchangeAmount"
          >
            <new-money-input
              id="exchange-amount"
              currency="USD"
              v-model="exchangeData.fromCurrencyAmount"
              v-if="exchangeData.fromCurrency === 'USD'"
            />
            <money-input
              id="exchange-amount"
              currency="IQD"
              v-model="exchangeData.fromCurrencyAmount"
              v-else
            />
          </el-form-item>
          <div class="mt-7">X</div>
          <el-form-item
            prop="exchangeRate"
            :label="t('exchangeRate')"
            class="label input-label"
            ref="exchangeRate"
          >
            <el-input v-model="exchangeData.exchangeRate" ref="exchangeRate" />
          </el-form-item>
        </div>

        <div>
          {{ t('exchange.amountIn') }}
          {{ formatMoney(exchangeData.toCurrencyAmount, exchangeData.toCurrency) }}
        </div>
      </div>

      <div class="input-section w-full">
        <div class="flex flex-col md:flex-row justify-start">
          <div style="width: 70%">
            <horizontal-button
              :title="$t('actions.saveChanges')"
              isSuccess
              faIcon="save"
              @click.prevent="openCashierPinDialog(formRef, registerTransaction)"
            />
          </div>
        </div>
      </div>
    </el-form>
  </div>
  <el-dialog v-model="cashierPinDialogOpen" width="30%">
    <div>
      <!-- Enter the pin -->
      <div class="my-2">
        <p>{{ t('dialogs.enterCashierPin') }}</p>
      </div>

      <!-- password input up to 6 chars -->
      <div class="my-2">
        <el-input
          id="password"
          v-model="cashierPin"
          type="password"
          required
          show-password
          autofocus
          dir="ltr"
          :maxlength="4"
          :minlength="4"
          ref="cashierPinInput"
          @keyup.enter="validateSubmit(formRef)"
        />
      </div>

      <!-- finish payment -->
      <div class="flex items-center justify-between mb-2">
        <horizontal-button
          class="text-lg complete-payment"
          :title="t('dialogs.completePay')"
          @click="validateSubmit(formRef)"
          :rounded="false"
        />
      </div>
    </div>
  </el-dialog>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue'
import HorizontalButton from '@/components/HorizontalButton.vue'
import AlertBadge from '@/components/AlertBadge.vue'
import { ElForm, FormInstance } from 'element-plus'
import MoneyInput from '@/components/form/MoneyInput.vue'
import { useI18n } from 'vue-i18n'
import { Transaction } from '@/models/accounting/Transaction'
import { Account } from '@/models/accounting/Account'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import NewMoneyInput from '@/components/form/NewMoneyInput.vue'
import { convertCurrency, formatMoney } from '@/utils/money'
import { nextTick } from 'vue'
import { ElRadioGroup, ElRadioButton } from 'element-plus'
import registerTransaction from '@/views/accounting/registers/RegisterTransaction.vue'
import {useSettingsStore} from "@/stores/settingsStore";

export default defineComponent({
  name: 'register-form',
  setup() {
    const messages = {
      en: {
        section: 'Register Transaction',
        transactionType: 'Transaction Type',
        amount: 'Amount',
        chooseAccountToAddMoney: 'Choose Account to Add Money From',
        chooseAccountTransferTo: 'Choose Account to Transfer Money To',
        selectAccount: 'Select Account',
        add: 'Add',
        withdraw: 'Withdraw',
        drop: 'Drop',
        foreignCurrency: 'Foreign Currency',
        exchangeRate: 'Exchange Rate',
        exchange: {
          option: 'Currency Exchange',
          label: 'Choose your exchange currency',
          amountIn: 'Amount in',
          amountOut: 'Amount out',
          IQDtoUSD: 'IQD to USD',
          USDtoIQD: 'USD to IQD'
        },
        amountIsRequired: 'Amount cannot be 0'
      },
      ar: {
        section: 'عمليات صندوق النقد',
        transactionType: 'نوع العملية',
        amount: 'المبلغ',
        chooseAccountToAddMoney: 'اختر الحساب الذي ترغب بإضافة مبلغ منه',
        chooseAccountTransferTo: 'اختر الحساب الذي ترغب بتحويل مبلغ له',
        selectAccount: 'اختر حساب',
        add: 'اضف',
        withdraw: 'اسحب',
        drop: 'ودّع',
        foreignCurrency: 'العملة الاجنبية',
        exchangeRate: 'سعر التصريف',
        exchange: {
          option: 'تحويل العملة',
          label: 'اختر عملة التصريف',
          amountIn: 'المبلغ الداخل',
          amountOut: 'المبلغ الخارج',
          IQDtoUSD: 'من الدينار العراقي الى الدولار',
          USDtoIQD: 'من الدولار الى الدينار'
        },
        amountIsRequired: 'لا يمكن ان يكون المبلغ 0'
      }
    }
    const { t } = useI18n({ messages, useScope: 'global' })
    const rules = {
      journalType: [
        {
          required: true,
          message: t('validation.required'),
          trigger: 'blur'
        }
      ],
      // amountAmount: [
      //   {
      //     required: true,
      //     message: t('validation.required'),
      //     trigger: 'blur'
      //   },
      //   {
      //     type: 'number',
      //     min: 0
      //   }
      // ],
      debitedAccountID: [
        {
          required: true,
          message: t('validation.required'),
          trigger: 'blur'
        }
      ],
      creditedAccountID: [
        {
          required: true,
          message: t('validation.required'),
          trigger: 'blur'
        }
      ]
    }

    const cashierPinDialogOpen = ref(false)
    const cashierPin = ref('')
    const cashierPinInput = ref()
    const formRef = ref<FormInstance>()

    return {
      t,
      rules,
      cashierPinDialogOpen,
      cashierPin,
      cashierPinInput,
      formRef
    }
  },
  components: {
    NewMoneyInput,
    LoadingSpinner,
    HorizontalButton,
    AlertBadge,
    MoneyInput
  },

  props: {
    initRegisterTransaction: {
      type: Transaction,
      required: true
    },
    successMsg: {
      type: String,
      required: true
    },
    submitAction: {
      type: Function,
      required: true
    },
    submitExchangeAction: {
      type: Function,
      required: true
    },
    enableClearForm: {
      type: Boolean,
      default: false
    },
    clearFormOnSubmit: {
      type: Boolean,
      default: false
    }
  },

  async beforeMount() {
    try {
      this.registerAccount = Account.from(
        await this.$http.get<Account>(`${Account.ENDPOINT}/${this.$ctx.currentRegister.accountID}`)
      )
      this.safeAccount = Account.from(
        await this.$http.get<Account>(
          `${Account.ENDPOINT}/${this.$ctx.currentLocation.safeAccountID}`
        )
      )

      // Fetch safes and banks accounts
      const safesResp = await this.$http.get<Account[]>('accounting/accounts/safes?skip_balance=true')
      this.safes = safesResp.map((s) => Account.from(s))

      const banksResp = await this.$http.get<Account[]>('accounting/accounts/banks?skip_balance=true')
      this.banks = banksResp.map((b) => Account.from(b))

      this.registerTransaction.journalType = 'exchange'
    } catch (e) {
      this.error.title = e.title
      this.error.body = e.body
    } finally {
      this.loading = false
    }
  },

  data() {
    const accounts = new Array<Account>()
    const registerAccount = new Account()
    const safeAccount = new Account()
    const safes = new Array<Account>()
    const banks = new Array<Account>()
    return {
      registerAccount,
      accounts,
      safes,
      banks,
      registerTransaction: this.initRegisterTransaction,
      primaryCurrencyAmount: 0,
      foreignCurrencyAmount: 0,
      USDToIQDExchangeRate: this.$ctx.exchangeRates.IQD,
      foreignCurrency: this.$ctx.currency === 'IQD' ? 'USD' : 'IQD',
      foreignCurrencyPrecision: this.$ctx.currency == 'IQD' ? 2 : 0,
      error: { title: '', body: '' },
      safeAccount,
      loading: true,
      exchangeOption: 'usdToIqd',
      exchangeData: {
        fromCurrencyAmount: 0,
        fromCurrency: 'USD',
        fromCurrencyPrecision: 2,
        toCurrency: 'IQD',
        toCurrencyAmount: 0,
        toCurrencyPrecision: 0,
        exchangeRate: this.$ctx.exchangeRates.IQD,
        mainCurrency: this.$ctx.currency,
        registerAccountID: '',
        exchangeAccountID: ''
      }
    }
  },

  watch: {
    'exchangeData.fromCurrencyAmount': {
      handler() {
        this.computeExchangedAmount()
      }
    },
    'exchangeData.exchangeRate': {
      handler() {
        this.computeExchangedAmount()
      }
    }
  },

  methods: {
    formatMoney,
    convertCurrency,
    computeExchangedAmount() {
      this.exchangeData.toCurrencyAmount = this.convertCurrency(
        this.exchangeData.fromCurrencyAmount,
        this.exchangeData.exchangeRate,
        this.exchangeData.fromCurrencyPrecision,
        this.exchangeData.toCurrency
      )
    },
    changeExchangeCurrency() {
      if (this.exchangeOption === 'usdToIqd') {
        this.exchangeData = {
          fromCurrencyAmount: 0,
          fromCurrency: 'USD',
          fromCurrencyPrecision: 2,
          toCurrency: 'IQD',
          toCurrencyAmount: 0,
          toCurrencyPrecision: 0,
          exchangeRate: this.$ctx.exchangeRates.IQD,
          mainCurrency: this.$ctx.currency,
          registerAccountID: '',
          exchangeAccountID: ''
        }
      } else {
        this.exchangeData = {
          fromCurrencyAmount: 0,
          fromCurrency: 'IQD',
          fromCurrencyPrecision: 0,
          toCurrency: 'USD',
          toCurrencyAmount: 0,
          toCurrencyPrecision: 2,
          exchangeRate: this.$ctx.exchangeRates.USD,
          mainCurrency: this.$ctx.currency,
          registerAccountID: '',
          exchangeAccountID: ''
        }
      }
    },
    async searchAccounts(query: string) {
      if (!query) {
        return
      }

      try {
        const url = `${Account.ENDPOINT}?q=${query}`
        const accounts = await this.$http.get<Account[]>(url)
        this.accounts = accounts
      } catch (error) {
        this.$alertModal.showDanger({ title: error.title })
        this.accounts = []
      }
    },

    clearForm(formName: string) {
      const form = this.$refs[formName] as typeof ElForm
      this.$globalEmitter.emit('money-input-reset')
      if (form) form.resetFields()
    },

    async preparePayload() {
      if (this.registerTransaction.journalType === 'exchange') {
        this.exchangeData.registerAccountID = this.registerAccount.id
        this.exchangeData.exchangeAccountID = this.$ctx.currentRegister.currencyExchangeAccountID

        return
      }

      this.registerTransaction.preparePayload(
        this.$ctx.currentLocation,
        this.$ctx.currentRegister,
        this.$ctx.currentUser
      )
      this.registerTransaction.currency = this.registerTransaction.amountDinero.getCurrency()

      this.registerTransaction.amountAmount =
        this.primaryCurrencyAmount +
        convertCurrency(
          this.foreignCurrencyAmount,
          this.USDToIQDExchangeRate,
          this.foreignCurrencyPrecision,
          'IQD'
        )
      this.registerTransaction.currency = this.$ctx.currency
      this.registerTransaction.precision = this.$ctx.currencyPrecision
      this.registerTransaction.multiCurrencyBreakdown = [
        {
          currency: this.$ctx.currency,
          precision: this.$ctx.currencyPrecision,
          amount: this.primaryCurrencyAmount
        },
        {
          currency: this.foreignCurrency,
          precision: this.foreignCurrencyPrecision,
          amount: this.foreignCurrencyAmount,
          exchangeRate: this.USDToIQDExchangeRate
        }
      ]

      if (this.registerTransaction.journalType === 'add') {
        this.registerTransaction.debitedAccountID = this.registerAccount.id
        this.registerTransaction.journalType = 'deposit'
        this.registerTransaction.label = 'registers_add'
      }

      if (this.registerTransaction.journalType === 'withdraw') {
        this.registerTransaction.creditedAccountID = this.registerAccount.id
        this.registerTransaction.journalType = 'withdrawal'
        this.registerTransaction.label = 'registers_withdraw'
      }

      if (this.registerTransaction.journalType === 'drop') {
        this.registerTransaction.journalType = 'transfer'
        this.registerTransaction.label = 'registers_drop'
        try {
          this.registerTransaction.creditedAccountID = this.registerAccount.id
          this.registerTransaction.debitedAccountID = this.$ctx.currentLocation.safeAccountID
        } catch (error) {
          this.$alertModal.showDanger({ title: error.title })
          return
        }
      }
    },

    async validateSubmit(formEl: FormInstance | undefined) {
      if (!formEl) return

      this.error.title = ''
      if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
        if (this.cashierPin === '') {
          this.cashierPinDialogOpen = false
          this.error.title = this.t('dialogs.errCashierPinRequired')
          return
        }
      }

      const amountValue =
        this.registerTransaction.journalType === 'exchange'
          ? this.exchangeData.fromCurrencyAmount
          : this.primaryCurrencyAmount + this.foreignCurrencyAmount

      if (amountValue <= 0) {
        this.cashierPinDialogOpen = false
        this.error.title = this.t('amountIsRequired')
        return false
      }

      this.preparePayload()

      const resp =
        this.registerTransaction.journalType === 'exchange'
          ? await this.submitExchangeAction(this.exchangeData, this.cashierPin)
          : await this.submitAction(this.registerTransaction, this.cashierPin)
      this.cashierPinDialogOpen = false
      if (resp) {
        this.error.title = resp?.title
        this.error.body = resp?.body
        document.getElementById('main')?.scrollTo({ top: 0 })
        return false
      } else {
        this.$alertModal.showSuccess({ title: this.successMsg, body: '' })
        location.reload()
      }
      return true
    },

    openCashierPinDialog(
        formEl: FormInstance | undefined,
        registerTransaction: Transaction
    ) {
      if (!formEl) return

      if (registerTransaction.journalType === 'exchange') {
        if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
          this.cashierPinDialogOpen = true
          nextTick(() => {
            setTimeout(() => {
              this.cashierPinInput.focus()
            }, 0)
          })
          return;
        }
        this.validateSubmit(this.formRef)

        return
      }

      formEl?.validate(async (valid: boolean) => {
        if (!valid) {
          document.getElementById('main')?.scrollTo({top: 0})
          return false
        } else {
          if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
            this.cashierPinDialogOpen = true
            nextTick(() => {
              setTimeout(() => {
                this.cashierPinInput.focus()
              }, 0)
            })
            return;
          }
          this.validateSubmit(this.formRef)
        }
      })
    }
  }
})
</script>

<style scoped>
.input-group {
  @apply flex flex-col w-full items-center;
}

.input-section {
  @apply rounded w-full justify-center;
}

@screen md {
  .input-section {
    @apply flex-row;
  }

  .input-group {
    @apply flex-row;
  }
}
</style>
